import React, { useState } from "react";
import last from "lodash/last";
import { Submission } from "src/scenes/PatientProfile/Forms/GQLForms/types";
import ClinicalPhotoView from "src/shared/ClinicalPhotos/View";
import { SelectedObj } from "src/types/types";
import { Embed, SummaryHeaderRow, SummaryHeaderText } from "../styles";
import { formatDateForTimeline } from "../util";
import SummaryHeaderDropdown from "./SummaryHeaderDropdown";

type SubmissionFile = Submission["files"][number];

export interface Props {
  patientID: string;
  sortedSubmissions: Array<Submission>;
  focusedSubmissionID: string | null;
}

export interface ViewSettings {
  groups: Map<string, Array<SubmissionFile>>;
  submissionID: string;
}

export function getViewSettings(
  sortedSubmissions: Array<Submission>,
  preferredSubmissionID: string | null
): ViewSettings | null {
  const isClinicalPhoto = ({ type }: SubmissionFile) =>
    type === "ClinicalPhoto";
  const eligibleSubmissions = sortedSubmissions.filter(({ files }) =>
    files.find(isClinicalPhoto)
  );
  const foundSubmission = eligibleSubmissions.find(
    ({ id }) => id === preferredSubmissionID
  );
  const lastSubmission = last(eligibleSubmissions);
  const groups = new Map(
    eligibleSubmissions.map(({ id, files }) => [
      id,
      files.filter(isClinicalPhoto),
    ])
  );
  if (groups.size === 0) {
    return null;
  }

  if (foundSubmission) {
    return { groups, submissionID: foundSubmission.id };
  }

  if (lastSubmission) {
    return { groups, submissionID: lastSubmission.id };
  }

  return null;
}

export function getDropdownOptions(
  sortedSubmissions: Array<Submission>,
  groups: Map<string, Array<SubmissionFile>>
): Array<SelectedObj> {
  return sortedSubmissions
    .filter(({ id }) => groups.has(id))
    .map(({ id, approvedAt, submittedAt, createdAt, form }) => {
      const date = formatDateForTimeline(
        approvedAt || submittedAt || createdAt
      );
      const label = `${form.name} - ${date}`;
      return { value: id, label };
    });
}

export default function ClinicalPhotos({
  patientID,
  sortedSubmissions,
  focusedSubmissionID,
}: Props): React.ReactElement | null {
  const [selectedSubmissionID, setSelectedSubmissionID] = useState<
    string | null
  >(null);
  const viewSettings = getViewSettings(
    sortedSubmissions,
    selectedSubmissionID || focusedSubmissionID
  );
  if (!viewSettings) {
    return null;
  }
  const { groups, submissionID } = viewSettings;
  const options = getDropdownOptions(sortedSubmissions, groups);

  return (
    <>
      <SummaryHeaderRow>
        <SummaryHeaderText>Clinical Photos</SummaryHeaderText>
        <SummaryHeaderDropdown
          options={options}
          selected={submissionID}
          onSelect={value => setSelectedSubmissionID(value)}
        />
      </SummaryHeaderRow>

      <Embed key={submissionID}>
        <ClinicalPhotoView
          patientID={patientID}
          files={groups.get(submissionID) as Submission["files"]}
        />
      </Embed>
    </>
  );
}
